import React, { useState } from 'react';
import PropTypes from 'prop-types';
import VisibilitySensor from 'react-visibility-sensor';
import SvgIcon from 'components/SvgIcon/SvgIcon';
import styles from './PricingBreakdownTable.module.scss';
import {
  getPitchLimitTestVariant,
  getStarterPitchAllowanceCopy,
} from 'helpers/PitchLimitTestHelper';

const buildFeatures = (variant, starterPitchCopy) => [
  {
    category: 'Core Features',
    items: [
      {
        title: variant === 'b' ? 'Job applications' : 'Job applications per month',
        description:
          'The ability to apply to jobs posted on Twine. Starter accounts have limits on which jobs they can apply to.',
        starter: starterPitchCopy,
        pro: variant === 'b' ? '50 per month' : '50',
        premium: 'Unlimited',
      },
      {
        title: 'Visibility to clients',
        description:
          'How you appear in search results to clients looking for freelancers.',
        starter: 'Standard',
        pro: 'Increased',
        premium: 'Premium',
      },
      {
        title: 'Job alerts',
        description: 'Instant notifications of new relevant jobs on Twine.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Portfolio',
        description:
          'Display your work on your portfolio to be viewed by clients.',
        starter: true,
        pro: true,
        premium: true,
      },
    ],
  },
  {
    category: 'Payments',
    items: [
      {
        title: 'Transaction fees',
        description:
          'Transaction fees are a percentage of your earnings and depend on what payout method you choose. Pro and Premium members enjoy zero transaction fees.',
        starter: 'Up to 6%',
        pro: '0%',
        premium: '0%',
      },
      {
        title: 'International Payouts',
        description:
          'International payouts are available to all members via several common payment platforms.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Twine Vault',
        description:
          'Use Twine Vault, an escrow-like service, for added security when working on a project with a client.',
        starter: true,
        pro: true,
        premium: true,
      },
      {
        title: 'Off-platform payments',
        description:
          'Directly invoice clients to use payment platforms of your choice. We do not restrict you.',
        starter: true,
        pro: true,
        premium: true,
      },
    ],
  },
  {
    category: 'Pro Features',
    items: [
      {
        title: 'Pro badge',
        description:
          'Stand out from the crowd and increase your chance of being hired.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'Contact clients directly',
        description:
          'Win more work with the direct email, WhatsApp or phone number of clients who share their details.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'One-page website',
        description:
          'A sleek, professional one-page website to showcase your best work.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'AI cover letter writer',
        description:
          'A tool to help you write compelling cover letters, pitches, and proposals.',
        starter: false,
        pro: true,
        premium: true,
      },
      {
        title: 'Priority support',
        description:
          'Any support request is handled with priority over Starter members.',
        starter: false,
        pro: true,
        premium: true,
      },
    ],
  },
];

const PricingBreakdownTable = ({ user }) => {
  const [expanded, setExpanded] = useState({});
  const pitchLimitVariant = getPitchLimitTestVariant(user);
  const features = buildFeatures(pitchLimitVariant, getStarterPitchAllowanceCopy(pitchLimitVariant));
  const [visible, setVisible] = useState(false);

  const toggleExpand = (title) => {
    setExpanded((prev) => ({ ...prev, [title]: !prev[title] }));
  };

  const renderValue = (value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <SvgIcon icon="tick" size="20" className={styles.checkIcon} />
      ) : (
        <span className={styles.dash}>-</span>
      );
    }
    return <p className={styles.featureText}>{value}</p>;
  };

  return (
    <VisibilitySensor
      partialVisibility
      delayedCall
      minTopValue={visible ? 0 : 100}
      active={!visible}
      onChange={(isVisible) => setVisible(isVisible)}
    >
      <div className={styles.container}>
        <h3 className={`${styles.header} ${visible ? '' : styles.hide}`}>
          Compare features
        </h3>
        {features.map((category) => (
          <>
            <div className={styles.categoryRow}>
              <h3 className={styles.categoryTitle}>{category.category}</h3>
            </div>
            <div className={styles.table}>
              <div className={styles.planTitles}>
                <div />
                <div className={styles.planTitle}>Starter</div>
                <div className={styles.planTitle}>Pro</div>
                <div className={styles.planTitle}>Premium</div>
              </div>
              <React.Fragment key={category.category}>
                {/* <div className={styles.categoryRow}>
                  <h3 className={styles.categoryTitle}>{category.category}</h3>
                </div> */}
                {category.items.map((item) => (
                  <div key={item.title} className={styles.featureWrapper}>
                    <div
                      className={styles.featureRow}
                      onClick={() => toggleExpand(item.title)}
                    >
                      <div className={styles.featureTitleCell}>
                        <SvgIcon
                          icon="chevron-down-narrow"
                          size="20"
                          className={`${styles.chevron} ${
                            expanded[item.title] ? styles.expanded : ''
                          }`}
                        />
                        <span className={styles.featureTitle}>
                          {item.title}
                        </span>
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.starter)}
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.pro)}
                      </div>
                      <div className={styles.planCell}>
                        {renderValue(item.premium)}
                      </div>
                    </div>
                    {expanded[item.title] && (
                      <div className={styles.descriptionRow}>
                        <div className={styles.description}>
                          <p>{item.description}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </React.Fragment>
            </div>
          </>
        ))}
      </div>
    </VisibilitySensor>
  );
};

PricingBreakdownTable.propTypes = {
  user: PropTypes.object,
};

PricingBreakdownTable.defaultProps = {
  user: null,
};

export default PricingBreakdownTable;
